import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform, useInView, animate, AnimatePresence } from "framer-motion";
import { ArrowUpRight, Star, Quote, ShieldCheck } from "lucide-react";
import { IMG, DOMESTIC_PACKAGES, INTERNATIONAL_PACKAGES, TESTIMONIALS, WHY_US, MARQUEE_ITEMS, durationLabel, packageTripType } from "../data/packages";
import Marquee from "../components/Marquee";
import Reveal, { EASE } from "../components/Reveal";
import PackageModal from "../components/PackageModal";
import { useEnquiry } from "../components/EnquiryModal";

function MaskedLine({ children, delay }) {
  return (
    <span className="block overflow-hidden pb-1">
      <motion.span
        className="block"
        initial={{ y: "112%" }}
        animate={{ y: "0%" }}
        transition={{ duration: 0.95, delay, ease: EASE }}
      >
        {children}
      </motion.span>
    </span>
  );
}

function Counter({ value, suffix = "", decimals = 0 }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-40px" });
  const [n, setN] = useState(0);
  useEffect(() => {
    if (!inView) return;
    const c = animate(0, value, {
      duration: 2,
      ease: [0.16, 1, 0.3, 1],
      onUpdate: (v) => setN(v),
    });
    return () => c.stop();
  }, [inView, value]);
  return (
    <span ref={ref} data-testid="stat-value">
      {n.toLocaleString("en-IN", { minimumFractionDigits: decimals, maximumFractionDigits: decimals })}
      {suffix}
    </span>
  );
}

const FEATURED = [DOMESTIC_PACKAGES[0], INTERNATIONAL_PACKAGES[0], INTERNATIONAL_PACKAGES[1], DOMESTIC_PACKAGES[2]];
const FEATURED_SPANS = [
  "min-h-[320px] md:col-span-2 md:row-span-2 md:min-h-0",
  "min-h-[240px] md:col-span-2",
  "min-h-[240px] md:col-span-1",
  "min-h-[240px] md:col-span-1",
];

const STATS = [
  { value: 12000, suffix: "+", label: "Happy Explorers" },
  { value: 42, suffix: "", label: "Destinations Curated" },
  { value: 4.9, suffix: "", decimals: 1, label: "Average Trip Rating" },
  { value: 100, suffix: "%", label: "Safe Departures" },
];

export default function Home() {
  const { openEnquiry } = useEnquiry();
  const [sel, setSel] = useState(null);
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "20%"]);
  const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  const stars = useMemo(
    () =>
      Array.from({ length: 64 }, (_, i) => ({
        left: (i * 37.7 + 11) % 100,
        top: (i * 23.3 + 7) % 100,
        size: (i % 3) + 1,
        delay: (i % 14) * 0.5,
        dur: 2.4 + (i % 5) * 0.8,
      })),
    []
  );

  return (
    <div data-testid="page-home">
      {/* ============ HERO ============ */}
      <section ref={heroRef} className="relative flex min-h-screen items-center overflow-hidden">
        <motion.div style={{ y: bgY }} className="absolute inset-0 scale-110">
          <img src={IMG.hero} alt="Starry night over a cinematic landscape" className="h-full w-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-b from-ink/75 via-ink/50 to-ink" />
          <div className="absolute inset-0 bg-gradient-to-r from-ink/85 via-ink/20 to-ink/50" />
        </motion.div>

        <div className="pointer-events-none absolute inset-0 hidden md:block" aria-hidden="true">
          {stars.map((s, i) => (
            <span
              key={i}
              className="absolute rounded-full bg-slate-100"
              style={{
                left: `${s.left}%`,
                top: `${s.top}%`,
                width: s.size,
                height: s.size,
                animation: `twinkle ${s.dur}s ease-in-out ${s.delay}s infinite`,
              }}
            />
          ))}
        </div>

        <svg
          viewBox="0 0 100 100"
          aria-hidden="true"
          className="animate-spin-slow pointer-events-none absolute -right-40 top-1/2 hidden h-[44rem] w-[44rem] -translate-y-1/2 opacity-25 lg:block"
          fill="none"
        >
          <circle cx="50" cy="50" r="46" stroke="#00F5D4" strokeWidth="0.25" strokeDasharray="1 3" />
          <ellipse cx="50" cy="50" rx="42" ry="16" transform="rotate(-25 50 50)" stroke="#F4A261" strokeWidth="0.3" />
          <circle cx="50" cy="50" r="9" fill="#00F5D4" opacity="0.5" />
        </svg>

        <motion.div style={{ opacity: fade }} className="relative z-10 mx-auto w-full max-w-7xl px-6 pb-24 pt-32">
          <motion.p
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.1, ease: EASE }}
            className="font-mono text-[10px] uppercase tracking-[0.35em] text-aurora md:text-xs"
          >
            Cosmos Routes · Tours & Bespoke Journeys
          </motion.p>

          <h1 className="mt-6 font-display text-[clamp(2rem,8vw,6.5rem)] font-extrabold leading-[0.98] tracking-[-0.03em] text-slate-50">
            <MaskedLine delay={0.25}>
              EXPLORE<span className="text-aurora">.</span>
            </MaskedLine>
            <MaskedLine delay={0.4}>
              EXPERIENCE<span className="text-aurora">.</span>
            </MaskedLine>
            <MaskedLine delay={0.55}>
              <em className="font-editorial font-medium italic tracking-[-0.01em] text-comet">BELONG.</em>
            </MaskedLine>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.85, ease: EASE }}
            className="mt-7 max-w-xl text-base leading-relaxed text-slate-300 md:text-lg"
          >
            Curated expeditions across India and the world — small groups, bespoke routes, and journeys
            that stay with you long after you are home.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 1, ease: EASE }}
            className="mt-10 flex flex-wrap items-center gap-4"
          >
            <button
              data-testid="hero-plan-trip-btn"
              onClick={() => openEnquiry({ tripType: "Customised Trip" })}
              className="rounded-full bg-aurora px-8 py-4 text-sm font-bold text-ink transition-all duration-300 hover:shadow-[0_0_40px_rgba(0,245,212,0.45)] hover:-translate-y-0.5"
            >
              Plan My Trip
            </button>
            <Link
              to="/domestic-packages"
              data-testid="hero-explore-packages-btn"
              className="flex items-center gap-2 rounded-full border border-white/20 px-8 py-4 text-sm font-semibold text-slate-100 transition-all duration-300 hover:border-aurora/60 hover:text-aurora"
            >
              Explore Packages <ArrowUpRight className="h-4 w-4" />
            </Link>
          </motion.div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 1.2 }}
          className="absolute inset-x-0 bottom-0 z-10 hidden border-t border-white/10 bg-ink/50 backdrop-blur-md md:block"
          data-testid="hero-stats-strip"
        >
          <div className="mx-auto grid max-w-7xl grid-cols-4 divide-x divide-white/10 px-6">
            {STATS.map((s, si) => (
              <div
                key={s.label}
                className={`px-8 py-6 ${si === 0 ? "lg:pl-32" : ""} ${si === STATS.length - 1 ? "lg:pr-32" : ""}`}
              >
                <p className="font-display text-2xl font-extrabold text-slate-50">
                  <Counter value={s.value} suffix={s.suffix} decimals={s.decimals || 0} />
                </p>
                <p className="mt-1 font-mono text-[9px] uppercase tracking-[0.25em] text-slate-500">{s.label}</p>
              </div>
            ))}
          </div>
        </motion.div>
      </section>

      {/* ============ MARQUEE ============ */}
      <Marquee items={MARQUEE_ITEMS} />

      {/* ============ FEATURED BENTO ============ */}
      <section className="mx-auto max-w-7xl px-6 py-24 md:py-32" data-testid="featured-section">
        <Reveal>
          <div className="flex flex-wrap items-end justify-between gap-6">
            <div>
              <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-aurora">Featured Expeditions</p>
              <h2 className="mt-4 font-display text-3xl font-extrabold tracking-tight text-slate-50 sm:text-4xl lg:text-5xl">
                Journeys our travellers <em className="font-editorial font-medium italic text-comet">swear by</em>
              </h2>
            </div>
            <Link to="/international-packages" data-testid="featured-view-all-btn" className="flex items-center gap-2 text-sm font-semibold text-aurora transition-all hover:gap-3">
              View all packages <ArrowUpRight className="h-4 w-4" />
            </Link>
          </div>
        </Reveal>

        <div className="mt-12 grid grid-cols-1 gap-5 md:grid-cols-4 md:auto-rows-[250px]">
          {FEATURED.map((pkg, i) => (
            <Reveal key={pkg.name} delay={i * 0.08} className={FEATURED_SPANS[i]}>
              <div
                data-testid={`featured-card-${i}`}
                onClick={() => setSel(pkg)}
                className="group relative h-full w-full cursor-pointer overflow-hidden rounded-3xl border border-white/10 transition-all duration-500 hover:border-aurora/40 hover:shadow-[0_20px_60px_rgba(0,245,212,0.12)]"
              >
                <img
                  src={pkg.image}
                  alt={pkg.name}
                  loading="lazy"
                  className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/25 to-transparent" />
                <span className="glass absolute left-5 top-5 rounded-full px-3 py-1.5 font-mono text-[10px] tracking-[0.18em] text-slate-100">
                  {durationLabel(pkg)}
                </span>
                <div className="absolute inset-x-5 bottom-5">
                  <p className="font-mono text-[9px] uppercase tracking-[0.25em] text-aurora">{pkg.region}</p>
                  <div className="mt-2 flex items-end justify-between gap-4">
                    <h3 className="font-display text-xl font-bold text-slate-50 md:text-2xl">{pkg.name}</h3>
                    <span className="shrink-0 font-display text-base font-bold text-comet">
                      ₹{pkg.price.toLocaleString("en-IN")}
                    </span>
                  </div>
                  <div className="mt-3 flex items-center gap-2 overflow-hidden opacity-0 transition-all duration-500 group-hover:opacity-100">
                    <span className="flex items-center gap-1.5 rounded-full bg-aurora px-4 py-2 text-xs font-bold text-ink">
                      View Details <ArrowUpRight className="h-3.5 w-3.5" />
                    </span>
                  </div>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* ============ WHY US ============ */}
      <section className="border-y border-white/5 bg-obsidian/40" data-testid="why-us-section">
        <div className="mx-auto grid max-w-7xl gap-14 px-6 py-24 md:py-32 lg:grid-cols-12">
          <div className="lg:col-span-5">
            <div className="lg:sticky lg:top-32">
              <Reveal>
                <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-aurora">Why Cosmos Routes</p>
                <h2 className="mt-4 font-display text-3xl font-extrabold leading-tight tracking-tight text-slate-50 sm:text-4xl lg:text-5xl">
                  Travel, raised to an{" "}
                  <em className="font-editorial font-medium italic text-comet">art form</em>
                </h2>
                <p className="mt-6 max-w-md text-base leading-relaxed text-slate-400">
                  We are route obsessives. Every itinerary is walked, eaten and slept in by our own team
                  before it ever reaches you — so the only surprise is how good it feels.
                </p>
                <button
                  data-testid="why-us-cta-btn"
                  onClick={() => openEnquiry({ tripType: "Customised Trip" })}
                  className="mt-8 rounded-full border border-aurora/40 px-7 py-3.5 text-sm font-semibold text-aurora transition-all duration-300 hover:bg-aurora hover:text-ink"
                >
                  Start the Conversation
                </button>
              </Reveal>
            </div>
          </div>
          <div className="lg:col-span-7">
            {WHY_US.map((w, i) => (
              <Reveal key={w.num} delay={i * 0.06}>
                <div className="group flex gap-8 border-t border-white/10 py-9 transition-all duration-500 last:border-b hover:bg-white/[0.02] hover:pl-4" data-testid={`why-us-item-${i}`}>
                  <span className="font-mono text-sm tracking-[0.2em] text-comet">{w.num}</span>
                  <div>
                    <h3 className="font-display text-xl font-bold text-slate-50 md:text-2xl">{w.title}</h3>
                    <p className="mt-3 max-w-lg text-sm leading-relaxed text-slate-400 md:text-base">{w.desc}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ============ STATS BAND (mobile) ============ */}
      <section className="mx-auto grid max-w-7xl grid-cols-2 gap-x-6 gap-y-10 px-6 py-16 md:hidden">
        {STATS.map((s) => (
          <div key={s.label} data-testid="mobile-stat">
            <p className="font-display text-3xl font-extrabold text-slate-50">
              <Counter value={s.value} suffix={s.suffix} decimals={s.decimals || 0} />
            </p>
            <p className="mt-1 font-mono text-[9px] uppercase tracking-[0.25em] text-slate-500">{s.label}</p>
          </div>
        ))}
      </section>

      {/* ============ TESTIMONIALS ============ */}
      <section className="mx-auto max-w-7xl px-6 py-24 md:py-32" data-testid="testimonials-section">
        <Reveal>
          <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-aurora">Explorer Stories</p>
          <h2 className="mt-4 max-w-2xl font-display text-3xl font-extrabold tracking-tight text-slate-50 sm:text-4xl lg:text-5xl">
            Words from the <em className="font-editorial font-medium italic text-comet">constellation</em>
          </h2>
        </Reveal>
        <div className="-mx-6 mt-12 flex snap-x snap-mandatory gap-5 overflow-x-auto px-6 pb-4 md:mx-0 md:px-0">
          {TESTIMONIALS.map((t, i) => (
            <Reveal key={t.name} delay={i * 0.07} className="min-w-[300px] snap-start sm:min-w-[380px]">
              <div className="flex h-full flex-col rounded-3xl border border-white/10 bg-obsidian p-8" data-testid={`testimonial-card-${i}`}>
                <Quote className="h-6 w-6 text-aurora/60" />
                <p className="mt-5 flex-1 font-editorial text-lg italic leading-relaxed text-slate-200">"{t.quote}"</p>
                <div className="mt-7 flex items-center justify-between border-t border-white/5 pt-5">
                  <div className="flex items-center gap-3">
                    <span className="flex h-10 w-10 items-center justify-center rounded-full border border-aurora/40 bg-aurora/10 font-display text-xs font-bold text-aurora">
                      {t.initials}
                    </span>
                    <div>
                      <p className="text-sm font-semibold text-slate-100">{t.name}</p>
                      <p className="text-xs text-slate-500">{t.location}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="flex gap-0.5">
                      {Array.from({ length: 5 }).map((_, s) => (
                        <Star key={s} className="h-3 w-3 fill-comet text-comet" />
                      ))}
                    </div>
                    <p className="mt-1 font-mono text-[9px] uppercase tracking-widest text-slate-500">{t.trip}</p>
                  </div>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* ============ CTA BANNER ============ */}
      <section className="mx-auto max-w-7xl px-6 pb-24 md:pb-32" data-testid="cta-banner">
        <Reveal>
          <div className="relative overflow-hidden rounded-[2.5rem] border border-aurora/25 bg-obsidian px-6 py-16 text-center md:py-24">
            <div className="pointer-events-none absolute -left-24 -top-24 h-72 w-72 rounded-full bg-aurora/15 blur-[110px]" />
            <div className="pointer-events-none absolute -bottom-24 -right-24 h-72 w-72 rounded-full bg-comet/15 blur-[110px]" />
            <div className="relative">
              <ShieldCheck className="mx-auto h-8 w-8 text-aurora" />
              <h2 className="mx-auto mt-6 max-w-2xl font-display text-3xl font-extrabold tracking-tight text-slate-50 sm:text-4xl lg:text-5xl">
                Ready to find <em className="font-editorial font-medium italic text-comet">your route?</em>
              </h2>
              <p className="mx-auto mt-5 max-w-md text-sm leading-relaxed text-slate-400 md:text-base">
                Tell us your dates and your dreams — a draft itinerary lands in your inbox within 24 hours.
              </p>
              <button
                data-testid="home-cta-plan-btn"
                onClick={() => openEnquiry({ tripType: "Customised Trip" })}
                className="mt-9 rounded-full bg-aurora px-9 py-4 text-sm font-bold text-ink transition-all duration-300 hover:shadow-[0_0_40px_rgba(0,245,212,0.45)] hover:-translate-y-0.5"
              >
                Plan My Trip
              </button>
              <p className="mt-5 font-mono text-[9px] uppercase tracking-[0.3em] text-slate-500">
                No obligations · Response within 24 hours
              </p>
            </div>
          </div>
        </Reveal>
      </section>

      <AnimatePresence>
        {sel && <PackageModal pkg={sel} onClose={() => setSel(null)} tripType={packageTripType(sel)} />}
      </AnimatePresence>
    </div>
  );
}
